import { useLayoutEffect, useState } from 'react'

import { clamp, getMinTop, VIEWPORT_MARGIN } from './viewportBounds'
import { readStoredWindowGeometry, saveWindowGeometry } from './windowGeometryStorage'

/**
 * Reopens the window where it was last closed, at the size it had, and saves both
 * again when it closes. A stored geometry is clamped to the current screen first:
 * it may come from a larger viewport or a different navbar height.
 * @param {Object} windowRef - Ref to the window element
 * @param {Function} setPosition - Position setter from useDraggable
 * @returns {boolean} - Whether a stored geometry was restored
 */
function useRememberedGeometry(windowRef, setPosition) {
  const [stored] = useState(readStoredWindowGeometry)

  useLayoutEffect(() => {
    const node = windowRef.current

    if (stored) {
      const style = window.getComputedStyle(node)
      const minWidth = parseFloat(style.minWidth) || 0
      const minHeight = parseFloat(style.minHeight) || 0
      const minTop = getMinTop()
      const width = clamp(stored.width, minWidth, window.innerWidth - VIEWPORT_MARGIN * 2)
      const height = clamp(stored.height, minHeight, window.innerHeight - VIEWPORT_MARGIN - minTop)
      node.style.width = `${width}px`
      node.style.height = `${height}px`
      setPosition({
        x: clamp(stored.x, VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN),
        y: clamp(stored.y, minTop, window.innerHeight - height - VIEWPORT_MARGIN),
      })
    }

    // A layout effect's cleanup runs before React detaches the ref and removes the
    // node, so the closing window can still be measured here (a passive effect's cannot).
    return () => {
      const { left, top } = node.getBoundingClientRect()
      saveWindowGeometry({ x: left, y: top, width: node.offsetWidth, height: node.offsetHeight })
    }
  }, [windowRef, stored, setPosition])

  return stored !== null
}

export default useRememberedGeometry
